/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { apiClient, ApiResponse, v3Client } from '@api/apiClient';
import { createEffect, createSignal } from 'solid-js';

import { useDebounceRequest } from './useDebounceRequest';

type AutoCompleteRenderType = {
  endpoint: string;
  version?: string;
  minCharacters?: number;
  useV3Client?: boolean;
};

export function useAutocomplete<T>(autoCompleteConfig: AutoCompleteRenderType) {
  const [searchTerm, setSearchTerm] = createSignal('');

  const { execute, isLoading, error, data, setData } = useDebounceRequest<
    T,
    string
  >(async (term: string) => {
    const { endpoint } = autoCompleteConfig;

    // Only make the request if we have enough characters
    const minChars = autoCompleteConfig.minCharacters ?? 2;

    if (term.length < minChars) {
      return null;
    }

    const url = `/${endpoint}${encodeURIComponent(term)}`;
    const client = autoCompleteConfig.useV3Client ? v3Client : apiClient;
    const response = (await client.get(url)) as unknown as ApiResponse<T>;
    const result = autoCompleteConfig.useV3Client
      ? response.data
      : response.value;

    return result ?? ([] as unknown as T);
  });

  // Effect to trigger search when input changes
  createEffect(() => {
    const term = searchTerm().trim();

    if (!!term) {
      void execute(term);
    }
  });

  return {
    searchTerm,
    setSearchTerm,
    results: data,
    setResults: setData,
    isLoading,
    error,
  };
}
